import { StyleSheet, Platform } from 'react-native';
import { getColors } from './ProfileScreen.colors';

const colors = getColors(true);
// Espelham constantes da ProfileScreen (mantidas em sincronia).
const PROFILE_WEEKLY_RING_SIZE = 80;
// Fundo do card de cabeçalho do perfil. Tom azul da MESMA família do gradiente
// do cover (COVER_GRADIENT em ProfileScreen.js: passa por #03386C e termina em
// #01172D), p/ o card emendar com o cover em vez de parecer outra superfície.
// Fixo nos 2 temas, como o cover.
const PROFILE_HEADER_BG = '#021F3C';
// Amarelo do card "Minha Posicao" (BenchmarkThermometerCard). Aplicado APENAS no
// icone da chama, no numero e no contorno do bloco de streak — fundo e barrinha
// de progresso seguem laranja. NAO usar colors.accentOrange aqui: aquele token
// tambem marca conquista de raridade epica, e essa semantica continua laranja.
const STREAK_YELLOW = '#FCF0B6';
const STREAK_YELLOW_RGB = '252, 240, 182';

// ── Compactação do cabeçalho na WEB ──────────────────────────────────────────
// No navegador de celular sobra bem menos altura que no app (a barra do Safari
// come ~190px dos 932px do iPhone 15 Pro Max). Medido: o cabeçalho ocupava
// ~465px de 745px de viewport — 62% da tela só para avatar/nome/bio/streak,
// empurrando "Desempenho Semanal" e o resto para fora.
//
// `W(web, nativo)` reduz APENAS os espaçamentos e corpos de fonte, mantendo a
// FORMA: mesma ordem, mesmos blocos, mesmas proporções relativas, mesmos raios
// e cores. Nada foi removido nem reposicionado. Em nativo o segundo argumento é
// devolvido tal e qual, então o visual do app não muda.
const IS_WEB = Platform.OS === 'web';
const W = (web, nativo) => (IS_WEB ? web : nativo);

export const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bgPrimary,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
  },
  // Profile Header
  profileHeader: {
    backgroundColor: PROFILE_HEADER_BG,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    marginBottom: W(10, 16),
    overflow: 'hidden',
  },
  coverGradient: {
    height: W(80, 100),
    position: 'relative',
  },
  coverPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
  },
  backBtn: {
    position: 'absolute',
    // `bottom` — NÃO trocar por `top`.
    // Os outros 3 botões do cabeçalho usam `bottom: 10`. Ancorar este pela mesma
    // borda mantém os quatro na mesma linha seja qual for a altura da capa.
    //
    // Antes era `top: 54`, derivado de uma capa de 100 (100 - 10 - 36). Só que a
    // capa é `W(80, 100)`: na WEB ela tem 80, então o voltar caía em 54..90 e os
    // outros em 34..70 — 20px de desalinhamento, visível no navegador e não no app.
    // Em nativo o resultado é idêntico ao anterior (100 - 10 - 36 = 54).
    bottom: 10,
    left: 16,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backIcon: {
    fontSize: 20,
    color: '#FFFFFF',
  },
  settingsBtn: {
    position: 'absolute',
    bottom: 10,
    right: 58,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  settingsIcon: {
    fontSize: 16,
  },
  themeToggleBtn: {
    position: 'absolute',
    bottom: 10,
    right: 16,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  themeToggleIcon: {
    fontSize: 18,
  },
  blockedBtn: {
    position: 'absolute',
    bottom: 10,
    right: 100,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileInfoContainer: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: W(14, 20),
    marginTop: W(-34, -50),
  },
  avatarWrapper: {
    position: 'relative',
  },
  avatarContainer: {
    width: W(76, 100),
    height: W(76, 100),
    borderRadius: W(38, 50),
    backgroundColor: colors.bgTertiary,
    borderWidth: 4,
    borderColor: PROFILE_HEADER_BG,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarEmoji: {
    fontSize: W(38, 50),
  },
  statusIndicator: {
    position: 'absolute',
    top: W(3, 5),
    right: W(3, 5),
    width: W(14, 18),
    height: W(14, 18),
    borderRadius: W(7, 9),
    borderWidth: 3,
    borderColor: PROFILE_HEADER_BG,
  },
  statusOnline: {
    backgroundColor: colors.accentSuccess,
  },
  avatarEditBtn: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: W(24, 30),
    height: W(24, 30),
    borderRadius: W(12, 15),
    backgroundColor: colors.accentPrimary,
    borderWidth: 2,
    borderColor: PROFILE_HEADER_BG,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarEditIcon: {
    fontSize: 12,
  },
  nameSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: W(8, 12),
    gap: 8,
  },
  userName: {
    fontSize: W(20, 24),
    fontWeight: '700',
    color: colors.textPrimary,
  },
  verifiedBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.accentPrimary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  verifiedIcon: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  premiumBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    gap: 4,
  },
  premiumIcon: {
    fontSize: 12,
  },
  premiumText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: W(4, 6),
    gap: 6,
  },
  username: {
    fontSize: 14,
    color: colors.accentPrimary,
    fontWeight: '500',
  },
  metaSeparator: {
    color: colors.textTertiary,
  },
  memberSince: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  bio: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: W(8, 12),
    lineHeight: W(18, 20),
    paddingHorizontal: 10,
  },
  streakContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    // Fundo mantido no laranja translucido original (so o conteudo virou amarelo).
    backgroundColor: 'rgba(255, 159, 67, 0.15)',
    borderWidth: 1,
    borderColor: `rgba(${STREAK_YELLOW_RGB}, 0.3)`,
    borderRadius: 12,
    paddingVertical: W(7, 10),
    paddingHorizontal: 16,
    marginTop: W(10, 16),
    gap: 8,
  },
  streakIcon: {
    fontSize: 20,
  },
  streakNumber: {
    fontSize: W(18, 22),
    fontWeight: '700',
    color: STREAK_YELLOW,
  },
  streakLabel: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  streakProgressBar: {
    width: 60,
    height: 5,
    backgroundColor: 'rgba(255, 159, 67, 0.2)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  streakProgressFill: {
    height: '100%',
    backgroundColor: colors.accentOrange,
    borderRadius: 3,
  },
  quickStats: {
    flexDirection: 'row',
    alignItems: 'center',
    // Ver nota em `actionButtons`. Esticada junto para as duas linhas
    // continuarem alinhadas entre si, como são hoje no app.
    alignSelf: W('stretch', 'auto'),
    marginTop: W(12, 20),
    gap: 10,
  },
  quickStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: W(8, 12),
    paddingHorizontal: 6,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.bgCard,
  },
  quickStatValue: {
    fontSize: W(17, 20),
    fontWeight: '700',
    color: colors.textPrimary,
  },
  quickStatLabel: {
    fontSize: 10,
    color: colors.textTertiary,
    textTransform: 'uppercase',
    marginTop: 4,
    letterSpacing: 0.5,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 6,
    // CAUSA do texto truncado na web. O pai (`profileInfoContainer`) tem
    // `alignItems: 'center'`, então esta linha encolhia para o conteúdo:
    // medido 273px dentro de um pai de 430px, sobrando 64px por botão para um
    // texto que precisa de 59–70px. O `flex: 1` de cada botão só distribui o
    // que a linha tem. No iOS o `adjustsFontSizeToFit` disfarçava encolhendo a
    // fonte; na web esse recurso não existe (é só-iOS), então saía
    // "Visão G...", "Editar P...", "Compar...".
    // Esticar devolve 93px por botão e o rótulo cabe inteiro.
    alignSelf: W('stretch', 'auto'),
    marginTop: W(12, 20),
  },
  // Botão compacto de largura igual (4 numa linha): texto auto-ajusta p/ caber.
  actionBtn: {
    flex: 1,
    paddingVertical: W(8, 10),
    paddingHorizontal: 4,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnText: {
    // `adjustsFontSizeToFit` nos 4 botoes (ProfileScreen.js ~l.787) e
    // EXCLUSIVO DO iOS — na web nao faz nada e o `numberOfLines={1}`
    // trunca: "Visao G...", "Editar P...", "Compar...". Reduzir o corpo
    // aqui reproduz na web o que o iOS faria sozinho.
    fontSize: W(12, 13),
    fontWeight: '600',
    textAlign: 'center',
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentPrimary,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
  },
  primaryBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
  },
  secondaryBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  // Weekly Performance Card
  weeklyCard: {
    borderRadius: 20,
    overflow: 'hidden',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.24,
    shadowRadius: 24,
    elevation: 8,
  },
  weeklyGradient: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  weeklyBody: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  weeklyLeft: {
    flex: 1,
    marginRight: 14,
  },
  weeklyTitles: {
    gap: 4,
  },
  weeklyTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    lineHeight: 16,
  },
  weeklySubtitle: {
    fontSize: 12,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 14,
    marginTop: 5,
  },
  weeklyActivityStats: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  weeklyActivityStat: {
    flex: 1,
  },
  weeklyStatValueRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 4,
  },
  weeklyStatValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: 24,
  },
  weeklyStatLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 14,
  },
  weeklyStatBar: {
    position: 'relative',
    width: '100%',
    height: 6,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.4)',
    overflow: 'hidden',
    marginTop: 4,
  },
  weeklyStatBarFill: {
    height: '100%',
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
  },
  weeklyRing: {
    position: 'relative',
    width: PROFILE_WEEKLY_RING_SIZE,
    height: PROFILE_WEEKLY_RING_SIZE,
  },
  weeklyRingInner: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  weeklyRingValue: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: 14,
  },
  weeklyRingLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 14,
  },
  // Stats Section
  statsSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 14,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  viewAllLink: {
    fontSize: 13,
    color: colors.accentPrimary,
    fontWeight: '600',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  statCard: {
    // Largura RELATIVA — não voltar para px calculado de `Dimensions`.
    //
    // Era `width: (SCREEN_WIDTH - 40) / 2`, com dois defeitos:
    //  1) `SCREEN_WIDTH` é lido de `Dimensions.get('window')` no CARREGAMENTO do
    //     módulo. Na web isso congela a largura da JANELA do navegador — que não
    //     é a largura da coluna do app — e não recalcula em resize/rotação.
    //  2) A conta ignorava o `gap: 8` da grade: dois cards somavam exatamente o
    //     espaço disponível, então com o gap NUNCA cabiam dois por linha.
    //
    // `flexBasis` percentual + `flexGrow` resolve os dois: os cards se dividem em
    // 2 por linha, crescem para ocupar a linha inteira e acompanham qualquer
    // largura de container, em qualquer plataforma.
    flexBasis: '47%',
    flexGrow: 1,
    minWidth: 0,
    backgroundColor: colors.bgSecondary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 12,
    padding: 10,
    flexDirection: 'row',
    gap: 8,
    overflow: 'hidden',
  },
  glassCard: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.10,
    shadowRadius: 6,
    elevation: 3,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.18)',
  },
  statCardIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  statCardEmoji: {
    fontSize: 20,
  },
  statCardContent: {
    flex: 1,
  },
  statCardValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  statCardLabel: {
    fontSize: 10,
    color: colors.textSecondary,
    marginTop: 1,
  },
  trendPositive: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 3,
  },
  trendText: {
    fontSize: 10,
    color: colors.accentSuccess,
    fontWeight: '500',
  },
  trendNeutral: {
    fontSize: 10,
    color: colors.textTertiary,
    marginTop: 3,
  },
  // Goal Section
  goalSection: {
    marginBottom: 20,
  },
  goalCard: {
    backgroundColor: colors.bgSecondary,
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    borderWidth: 1,
    borderColor: colors.border,
  },
  goalIcon: {
    fontSize: 36,
  },
  goalInfo: {
    flex: 1,
  },
  goalName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  goalDesc: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  goalProgress: {
    alignItems: 'flex-end',
  },
  goalProgressText: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.accentPrimary,
    marginBottom: 6,
  },
  goalProgressBar: {
    width: 80,
    height: 6,
    backgroundColor: colors.bgCard,
    borderRadius: 3,
    overflow: 'hidden',
  },
  goalProgressFill: {
    height: '100%',
    borderRadius: 3,
  },
  // Achievements Section
  achievementsSection: {
    marginBottom: 20,
  },
  achievementsScroll: {
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  achievementCard: {
    width: 140,
    backgroundColor: colors.bgTertiary,
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 12,
    padding: 14,
    marginRight: 12,
    alignItems: 'center',
    position: 'relative',
  },
  achievementUnlocked: {
    borderColor: colors.accentSuccess,
  },
  achievementLocked: {
    opacity: 0.5,
  },
  achievementRare: {
    borderColor: colors.accentPurple,
    backgroundColor: 'rgba(159, 122, 234, 0.1)',
  },
  achievementEpic: {
    borderColor: colors.accentOrange,
    backgroundColor: 'rgba(255, 159, 67, 0.1)',
  },
  rarityBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: colors.accentPurple,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  rarityEpic: {
    backgroundColor: colors.accentOrange,
  },
  rarityBadgeText: {
    fontSize: 8,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  achievementIconWrapper: {
    marginBottom: 8,
  },
  achievementIcon: {
    fontSize: 36,
  },
  achievementName: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: 4,
  },
  achievementDesc: {
    fontSize: 10,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 14,
  },
  achievementDate: {
    fontSize: 10,
    color: colors.textTertiary,
    marginTop: 6,
    fontStyle: 'italic',
  },
  achievementProgressContainer: {
    width: '100%',
    marginTop: 8,
  },
  achievementProgressBar: {
    width: '100%',
    height: 5,
    backgroundColor: colors.bgCard,
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 4,
  },
  achievementProgressFill: {
    height: '100%',
    backgroundColor: colors.accentPrimary,
    borderRadius: 3,
  },
  achievementProgressText: {
    fontSize: 10,
    color: colors.textTertiary,
    textAlign: 'center',
  },
  achievementPercentage: {
    fontSize: 9,
    color: colors.accentPurple,
    marginTop: 6,
    fontWeight: '500',
  },
  // Activity Section
  activitySection: {
    marginBottom: 20,
  },
  activityList: {
    gap: 10,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.bgTertiary,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 10,
    padding: 12,
    gap: 12,
  },
  activityIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activityIconSuccess: {
    backgroundColor: 'rgba(81, 207, 102, 0.15)',
  },
  activityIconAchievement: {
    backgroundColor: 'rgba(255, 215, 0, 0.15)',
  },
  activityIconComment: {
    backgroundColor: 'rgba(91, 143, 249, 0.15)',
  },
  activityIconStreak: {
    backgroundColor: 'rgba(255, 159, 67, 0.15)',
  },
  activityEmoji: {
    fontSize: 16,
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  activityTime: {
    fontSize: 11,
    color: colors.textTertiary,
    marginTop: 4,
  },
  activityBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
  },
  activityBadgeSuccess: {
    backgroundColor: 'rgba(81, 207, 102, 0.15)',
  },
  activityBadgeAchievement: {
    backgroundColor: 'rgba(255, 215, 0, 0.15)',
  },
  activityBadgeStreak: {
    backgroundColor: 'rgba(255, 159, 67, 0.15)',
  },
  activityBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  loadMoreBtn: {
    width: '100%',
    paddingVertical: 14,
    marginTop: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.border,
    borderRadius: 10,
    alignItems: 'center',
  },
  loadMoreText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  bottomPadding: {
    height: 40,
  },
  // Avatar Image style
  avatarImage: {
    width: 92,
    height: 92,
    borderRadius: 46,
  },
  // Modal Styles
  modalOverlay: {
    // SEM cor de fundo aqui de propósito — o escurecimento é o `modalDim`, uma
    // camada IRMÃ da folha. Quando a cor ficava neste container (que desliza com
    // `animationType="slide"`), o escuro subia junto com a folha em vez de fazer
    // fade parado. Mesmo padrão do CreatePostModal da Comunidade.
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalDim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.7)',
  },
  // Ocupa o espaço acima da folha e recebe o toque de "fechar ao clicar fora".
  // Separado do `modalDim` porque aquele tem `pointerEvents="none"` para não
  // roubar o toque enquanto faz fade.
  modalBackdrop: {
    flex: 1,
    width: '100%',
  },
  modalContainer: {
    backgroundColor: colors.bgSecondary,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '85%',
    paddingBottom: 40,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalCloseBtn: {
    fontSize: 20,
    color: colors.textSecondary,
    padding: 4,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  modalContent: {
    padding: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: 8,
    marginTop: 16,
  },
  textInput: {
    backgroundColor: colors.bgTertiary,
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    color: colors.textPrimary,
    borderWidth: 1,
    borderColor: colors.border,
  },
  bioInput: {
    height: 80,
    textAlignVertical: 'top',
  },
  charCount: {
    fontSize: 12,
    color: colors.textTertiary,
    textAlign: 'right',
    marginTop: 4,
  },
  usernameInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.bgTertiary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  usernamePrefix: {
    fontSize: 16,
    color: colors.textTertiary,
    paddingLeft: 14,
  },
  usernameInput: {
    flex: 1,
    padding: 14,
    fontSize: 16,
    color: colors.textPrimary,
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 6,
    marginTop: 8,
  },
  avatarGridItem: {
    width: '22%',
    aspectRatio: 1,
    borderRadius: 12,
    backgroundColor: colors.bgTertiary,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
    overflow: 'hidden',
  },
  avatarGridItemSelected: {
    borderColor: colors.accentPrimary,
    backgroundColor: 'rgba(91, 143, 249, 0.2)',
  },
  avatarGridImg: {
    width: '85%',
    height: '85%',
    borderRadius: 12,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    // Azul-base do cabeçalho desta tela (#03386C, o mesmo do COVER_GRADIENT),
    // não o roxo global da marca — dentro do Perfil os botões acompanham a capa.
    backgroundColor: '#03386C',
    borderRadius: 12,
    padding: 16,
    marginTop: 24,
    marginBottom: 40,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  // Exclusão de conta (zona de perigo + modal de confirmação)
  deleteAccountButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    marginBottom: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(229, 57, 53, 0.4)',
  },
  deleteAccountButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#E53935',
  },
  deleteModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  deleteModalCard: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: colors.bgSecondary,
    borderRadius: 20,
    padding: 22,
  },
  deleteModalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  deleteModalMessage: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 16,
  },
  deleteModalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 18,
  },
  deleteModalCancelBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  deleteModalCancelText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  deleteModalConfirmBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: '#E53935',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteModalConfirmText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  // Photo Section Styles
  photoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: colors.bgTertiary,
    borderRadius: 16,
    marginTop: 8,
    gap: 16,
  },
  photoPreview: {
    width: 80,
    height: 80,
    borderRadius: 40,
    overflow: 'hidden',
    backgroundColor: colors.bgPrimary,
    borderWidth: 2,
    borderColor: colors.accentPrimary,
  },
  photoPreviewImg: {
    width: '100%',
    height: '100%',
  },
  photoButtons: {
    flex: 1,
    gap: 8,
  },
  photoBtn: {
    // Ver nota em `saveButton`: azul-base do cabeçalho do Perfil.
    backgroundColor: '#03386C',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    alignItems: 'center',
  },
  photoBtnText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  photoRemoveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,107,107,0.2)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 10,
  },
  photoRemoveBtnText: {
    color: '#FF6B6B',
    fontWeight: '600',
    fontSize: 13,
  },
});
